"use client";

import dayjs from "dayjs";

import { ErrorView } from "@/components/error-view";
import { Translation } from "@/data/lang";
import {
  dollarFormatter,
  percentageFormatter,
} from "@/features/analysis/utils/formatters";
import {
  PERatio,
  PEStream,
  ValueIndicator,
  ValueIndicatorData,
  ValueIndicatorType,
} from "@/features/analysis/value/value-factor-charts/services/value-indicator";
import { useLang } from "@/hooks/use-lang";

import { BarChart } from "ui/analysis/bar-chart";
import {
  LineChart,
  LineChartData,
  LineChartInfo,
} from "ui/analysis/line-chart";
import { StackAreaLineChart } from "ui/analysis/stack-area-line-chart";

import { ValueFactorInterval } from "../../value-factor-interval/data/intervals";
import { ValueFactorRange } from "../../value-factor-range/data/value-factor-range";

type ValueForceFactorChartProps = {
  data: ValueIndicatorType;
  indicator: ValueIndicator;
  interval: ValueFactorInterval;
  range: ValueFactorRange;
};

const indicatorReturnKey = {
  ["REVENUE-YOY"]: "REVENUE_YOY_",
  ["NCFO-NETINC"]: "NCFO_NETINC_ratio_",
  ["EPS"]: "EPS_",
  ["ROE"]: "ROE_",
  ["ROA"]: "ROA_",
  ["ROS"]: "ROS_",
  ["ROIC"]: "ROIC_",
  ["GROSS_MARGIN"]: "GROSS_MARGIN_",
  ["NET_MARGIN"]: "NET_MARGIN_",
  ["OPERATING_MARGIN"]: "OPERATING_MARGIN_",
};

export const ValueFactorCharts = ({
  data,
  indicator,
  interval,
  range,
}: ValueForceFactorChartProps) => {
  const { t } = useLang();

  if (indicator === "PE-RATIO") {
    return generatePERatioChart({ t, data: data as PERatio });
  }

  if (indicator === "ROE-ROA-ROS-ROIC") {
    return generateRoChart({
      t,
      roa: prepareData({ data, indicator: "ROA", interval, range }),
      roe: prepareData({ data, indicator: "ROE", interval, range }),
      ros: prepareData({ data, indicator: "ROS", interval, range }),
      roic: prepareData({ data, indicator: "ROIC", interval, range }),
    });
  }

  if (indicator === "GPM-OPM-NPM") {
    return generateMarginChart({
      t,
      grossMargin: prepareData({
        data,
        indicator: "GROSS_MARGIN",
        interval,
        range,
      }),
      operatingMargin: prepareData({
        data,
        indicator: "OPERATING_MARGIN",
        interval,
        range,
      }),
      netMargin: prepareData({
        data,
        indicator: "NET_MARGIN",
        interval,
        range,
      }),
    });
  }

  if (["REVENUE-YOY", "NCFO-NETINC", "EPS"].includes(indicator)) {
    return generateBarChart({
      t,
      data: prepareData({ data, indicator, interval, range }),
      label: t.analysis.value.factor.value,
      formatter: indicator === "EPS" ? dollarFormatter : percentageFormatter,
    });
  }

  return <></>;
};

const prepareData = ({
  data,
  indicator,
  interval,
  range,
}: {
  data: ValueIndicatorType;
  indicator:
    | ValueIndicator
    | "ROE"
    | "ROA"
    | "ROS"
    | "ROIC"
    | "GROSS_MARGIN"
    | "OPERATING_MARGIN"
    | "NET_MARGIN";
  interval: ValueFactorInterval;
  range: ValueFactorRange;
}): ValueIndicatorData[] => {
  const indicatorKey = indicatorReturnKey[
    indicator as keyof typeof indicatorReturnKey
  ] as valueof<typeof indicatorReturnKey>;

  const timePeriodKey =
    interval === "annual" ? "MRY" : interval === "quarter4" ? "MRT" : "MRQ";
  const combinedKey = `${indicatorKey}${timePeriodKey}`;

  const indicatorData = data?.[
    combinedKey as keyof ValueIndicatorType
  ] as unknown as ValueIndicatorData[];

  /**
   * Note:
   * For example,
   * 5Y => get 2018 from 2023 (now) => filter data if it's after 2018
   */
  const filteredIndicatorData = indicatorData
    ?.filter(({ date }) =>
      date
        ? dayjs(date).isAfter(dayjs().subtract(parseInt(range), "year"))
        : false
    )
    .map((item) => ({
      ...item,
      score: indicator !== "EPS" ? (item?.score ?? 0) * 100 : item?.score ?? 0,
    }));

  return filteredIndicatorData;
};

const generatePERatioChart = ({
  t,
  data,
}: {
  t: Translation;
  data: PERatio;
}) => {
  const stackAreaLineChartData: LineChartData[] = [];
  const peStreams: { [key: string]: number }[] = [];

  for (let i = 1; i <= 6; i++) {
    const key = `PE_stream_${i}`;
    const peStream = data?.[key as keyof PERatio] as PEStream;

    const pe = peStream?.data?.reduce(
      (acc, cur) => ({
        ...acc,
        [cur?.date ?? ""]: cur?.score ?? 0,
      }),
      {}
    ) as { [key: string]: number };
    peStreams.push(pe);
  }

  const [pe1, pe2, pe3, pe4, pe5, pe6] = peStreams;

  data?.monthlyAvgPrice?.forEach(({ date, score }) => {
    if (!date) return;

    stackAreaLineChartData.push({
      name: date ?? "",
      [t.analysis.value.factor.averageMonthlyPrice]: +(score?.toFixed(2) ?? 0),
      [data?.PE_stream_1?.text ?? ""]: +pe1?.[date]?.toFixed(2),
      [data?.PE_stream_2?.text ?? ""]: +pe2?.[date]?.toFixed(2),
      [data?.PE_stream_3?.text ?? ""]: +pe3?.[date]?.toFixed(2),
      [data?.PE_stream_4?.text ?? ""]: +pe4?.[date]?.toFixed(2),
      [data?.PE_stream_5?.text ?? ""]: +pe5?.[date]?.toFixed(2),
      [data?.PE_stream_6?.text ?? ""]: +pe6?.[date]?.toFixed(2),
    });
  });

  const linesInfo: { [key: string]: LineChartInfo } = {
    ...(data?.PE_stream_1?.text && {
      [data?.PE_stream_1?.text ?? ""]: {
        lineType: "linear",
        lineStroke: "#22C55EB3",
        lineStrokeWidth: 5,
        lineStrokeDasharray: "",
      },
    }),
    [t.analysis.value.factor.averageMonthlyPrice]: {
      lineType: "linear",
      lineStroke: "#0369A1",
      lineStrokeWidth: 2,
      lineStrokeDasharray: "",
    },
  };

  const stackAreaInfo = {
    [data?.PE_stream_1?.text ?? ""]: { fill: "#ffffff" },
    [data?.PE_stream_2?.text ?? ""]: { fill: "#86EFAC" },
    [data?.PE_stream_3?.text ?? ""]: { fill: "#FDE047" },
    [data?.PE_stream_4?.text ?? ""]: { fill: "#EAB308" },
    [data?.PE_stream_5?.text ?? ""]: { fill: "#FCA5A5" },
    [data?.PE_stream_6?.text ?? ""]: { fill: "#EF4444" },
  };

  if (
    stackAreaLineChartData.length === 0 ||
    Object.keys(stackAreaLineChartData[0]).length <= 5
  )
    return <ErrorView errorType="empty">{t.error.noData}</ErrorView>;

  return (
    <StackAreaLineChart
      id="pe-ratio-chart"
      data={stackAreaLineChartData}
      linesInfo={linesInfo}
      stackAreasInfo={stackAreaInfo}
      tooltipReverseOrder
      tooltipIconType="circle"
      labelToHide={{
        [(data?.PE_stream_1?.text ?? "") + "_stack"]: "all",
      }}
      yTicksFormatter={dollarFormatter}
    />
  );
};

const generateBarChart = ({
  t,
  data,
  label,
  formatter,
}: {
  t: Translation;
  data: ValueIndicatorData[];
  label: string;
  formatter: (value: unknown) => string;
}) => {
  const barChartData = data?.map(({ date, score }) => ({
    name: date ?? "",
    [label]: +(score?.toFixed(2) ?? 0),
  }));

  const barChartInfo = new Map([[label, { barColor: "#0284C7" }]]);

  if (!barChartData || barChartData?.length === 0)
    return <ErrorView errorType="empty">{t.error.noData}</ErrorView>;

  return (
    <BarChart
      data={barChartData}
      info={barChartInfo}
      yAxisWidth={50}
      yTicksPaddingRight={24}
      yTicksFormatter={formatter}
    />
  );
};

const generateRoChart = ({
  t,
  roa,
  roe,
  ros,
  roic,
}: {
  t: Translation;
  roa: ValueIndicatorData[];
  roe: ValueIndicatorData[];
  ros: ValueIndicatorData[];
  roic: ValueIndicatorData[];
}) => {
  const data: LineChartData[] = [];
  for (let i = 0; i < roa?.length; i++) {
    data.push({
      name: roa?.[i]?.date ?? "",
      ROA: +(roa?.[i]?.score?.toFixed(2) ?? 0),
      ROE: +(roe?.[i]?.score?.toFixed(2) ?? 0),
      ROS: +(ros?.[i]?.score?.toFixed(2) ?? 0),
      ROIC: +(roic?.[i]?.score?.toFixed(2) ?? 0),
    });
  }

  const defaultInfo: LineChartInfo = {
    lineStrokeWidth: 2,
    lineType: "linear",
    lineStrokeDasharray: "1 0",
    lineStroke: "",
  };

  const info: { [key: string]: LineChartInfo } = {
    ROA: { ...defaultInfo, lineStroke: "#EF4444" },
    ROE: { ...defaultInfo, lineStroke: "#9333EA" },
    ROS: { ...defaultInfo, lineStroke: "#0EA5E9" },
    ROIC: { ...defaultInfo, lineStroke: "#22C55E" },
  };

  if (data.length === 0)
    return <ErrorView errorType="empty">{t.error.noData}</ErrorView>;

  return (
    <LineChart
      showDots
      id="ro-chart"
      data={data}
      linesInfo={info}
      showLegend
      tooltipIconType="circle"
      yTicksFormatter={percentageFormatter}
      showGridHorizontal
      gridStrokeWidth={1}
      gridStroke="#e4e4e7"
    />
  );
};

const generateMarginChart = ({
  t,
  grossMargin,
  operatingMargin,
  netMargin,
}: {
  t: Translation;
  grossMargin: ValueIndicatorData[];
  operatingMargin: ValueIndicatorData[];
  netMargin: ValueIndicatorData[];
}) => {
  const data: LineChartData[] = [];
  const availableMargins = new Set<string>();

  const marginKeys = {
    GPM: t.analysis.value.factor.margin.grossMargin,
    OPM: t.analysis.value.factor.margin.operatingMargin,
    NPM: t.analysis.value.factor.margin.netMargin,
  };

  for (
    let i = 0;
    i <
    Math.max(
      grossMargin?.length || 0,
      operatingMargin?.length || 0,
      netMargin?.length || 0
    );
    i++
  ) {
    const dataPoint: LineChartData = {
      name:
        grossMargin?.[i]?.date ??
        operatingMargin?.[i]?.date ??
        netMargin?.[i]?.date ??
        "",
    };

    if (grossMargin?.[i]) {
      dataPoint[marginKeys.GPM] = +(grossMargin[i].score ?? 0).toFixed(2);
      availableMargins.add(marginKeys.GPM);
    }
    if (operatingMargin?.[i]) {
      dataPoint[marginKeys.OPM] = +(operatingMargin[i].score ?? 0).toFixed(2);
      availableMargins.add(marginKeys.OPM);
    }
    if (netMargin?.[i]) {
      dataPoint[marginKeys.NPM] = +(netMargin[i].score ?? 0).toFixed(2);
      availableMargins.add(marginKeys.NPM);
    }
    data.push(dataPoint);
  }

  const defaultInfo: LineChartInfo = {
    lineStrokeWidth: 2,
    lineType: "linear",
    lineStrokeDasharray: "1 0",
    lineStroke: "",
  };

  const info: { [key: string]: LineChartInfo } = {
    ...(availableMargins.has(marginKeys.GPM) && {
      [marginKeys.GPM]: { ...defaultInfo, lineStroke: "#0EA5E9" },
    }),
    ...(availableMargins.has(marginKeys.OPM) && {
      [marginKeys.OPM]: { ...defaultInfo, lineStroke: "#EF4444" },
    }),
    ...(availableMargins.has(marginKeys.NPM) && {
      [marginKeys.NPM]: { ...defaultInfo, lineStroke: "#22C55E" },
    }),
  };

  if (data.length === 0 || availableMargins.size === 0) {
    return <ErrorView errorType="empty">{t.error.noData}</ErrorView>;
  }

  return (
    <LineChart
      showDots
      id="margin-chart"
      data={data}
      linesInfo={info}
      showLegend
      tooltipIconType="circle"
      yTicksFormatter={percentageFormatter}
      showGridHorizontal
      gridStrokeWidth={1}
      gridStroke="#e4e4e7"
    />
  );
};
